/**
 * AcreLatch Offline Queue
 * ------------------------
 * When the user hits POST /api/expenses|yields|mileage without a signal,
 * we stash the request in IndexedDB and return a synthetic success. On the
 * next `online` event (or manual Sync Now click) we replay every queued
 * entry in order and emit events the UI can hook into.
 *
 * No external libs — uses the raw IndexedDB API via a tiny Promise wrapper.
 */

const DB_NAME = "acrelatch-offline";
const DB_VERSION = 1;
const STORE = "mutations";

// Only queue mutations to these endpoints. Everything else errors as usual.
const QUEUEABLE_PATHS = [
  "/api/expenses",
  "/api/yields",
  "/api/mileage",
];

function isQueueable(method, url) {
  if (!["POST", "PUT"].includes(String(method).toUpperCase())) return false;
  // url is relative like "/expenses" after axios baseURL stripping or full absolute
  try {
    const path = url.startsWith("http") ? new URL(url).pathname : url;
    return QUEUEABLE_PATHS.some((p) => path.startsWith(p));
  } catch {
    return false;
  }
}

let dbPromise = null;
function openDb() {
  if (dbPromise) return dbPromise;
  dbPromise = new Promise((resolve, reject) => {
    const req = indexedDB.open(DB_NAME, DB_VERSION);
    req.onupgradeneeded = () => {
      const db = req.result;
      if (!db.objectStoreNames.contains(STORE)) {
        db.createObjectStore(STORE, { keyPath: "id", autoIncrement: true });
      }
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
  return dbPromise;
}

async function tx(mode, fn) {
  const db = await openDb();
  return new Promise((resolve, reject) => {
    const t = db.transaction(STORE, mode);
    const store = t.objectStore(STORE);
    const out = fn(store);
    t.oncomplete = () => resolve(out?.result ?? out);
    t.onerror = () => reject(t.error);
    t.onabort = () => reject(t.error);
  });
}

export async function enqueue(entry) {
  const record = {
    method: String(entry.method || "POST").toUpperCase(),
    url: entry.url,
    body: entry.body,
    headers: entry.headers || {},
    created_at: new Date().toISOString(),
  };
  await tx("readwrite", (s) => s.add(record));
  notifyChange();
  return record;
}

export async function listQueue() {
  return new Promise(async (resolve) => {
    const db = await openDb();
    const t = db.transaction(STORE, "readonly");
    const req = t.objectStore(STORE).getAll();
    req.onsuccess = () => resolve(req.result || []);
    req.onerror = () => resolve([]);
  });
}

async function clearById(id) {
  await tx("readwrite", (s) => s.delete(id));
  notifyChange();
}

/** Replay the queue. Returns {synced, failed}. */
export async function flushQueue(baseURL) {
  if (!navigator.onLine) return { synced: 0, failed: 0, offline: true };
  const items = await listQueue();
  let synced = 0;
  let failed = 0;
  for (const item of items) {
    try {
      const url = item.url.startsWith("http") ? item.url : `${baseURL}${item.url}`;
      const res = await fetch(url, {
        method: item.method,
        headers: { "Content-Type": "application/json", ...item.headers },
        body: item.body ? JSON.stringify(item.body) : undefined,
        credentials: "include",
      });
      if (res.ok) {
        await clearById(item.id);
        synced++;
      } else if (res.status === 401 || res.status === 403) {
        // Auth gone — stop so we don't drop records. User needs to sign in.
        failed++;
        break;
      } else if (res.status >= 400 && res.status < 500) {
        // Bad payload — don't retry forever. Drop and surface the error.
        await clearById(item.id);
        failed++;
      } else {
        failed++;
      }
    } catch {
      failed++;
    }
  }
  return { synced, failed };
}

// --- Event system so the UI can show a pending badge + toast ---
const listeners = new Set();
export function subscribe(fn) {
  listeners.add(fn);
  return () => listeners.delete(fn);
}
function notifyChange() {
  listeners.forEach((l) => {
    try { l(); } catch {}
  });
}

export const OfflineQueue = {
  isQueueable,
  enqueue,
  listQueue,
  flushQueue,
  subscribe,
};
